import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { FOLLOW_PACK_KIND, parseFollowPackEvent, type FollowPack } from '@/lib/followPacks';

export const PACK_LIST_LIMIT = 25;
export const PACK_PAGE_SIZES = [25, 50, 100] as const;

export type PackFilter = 'all' | 'following' | 'included' | 'mine';

interface UseFollowPacksOptions {
  filter?: PackFilter;
  /** Current user's pubkey (for mine/included filters). */
  userPubkey?: string;
  /** Pubkeys the current user follows (for following filter). */
  following?: string[];
  /** Pagination cursor: fetch packs older than this timestamp. */
  until?: number;
  limit?: number;
  enabled?: boolean;
}

export function useFollowPacks({
  filter = 'all',
  userPubkey,
  following,
  until,
  limit = PACK_LIST_LIMIT,
  enabled = true,
}: UseFollowPacksOptions = {}) {
  const { nostr } = useNostr();

  return useQuery<{ packs: FollowPack[]; hasMore: boolean }>({
    queryKey: ['follow-packs', filter, userPubkey ?? '', (following ?? []).join(','), until ?? 0, limit],
    queryFn: async (c) => {
      // Hoisted to a variable (not an inline literal) so extra `#p` tag
      // filters don't trip excess-property checks on the NostrFilter type.
      const f = {
        kinds: [FOLLOW_PACK_KIND],
        limit: Math.min(limit + 1, 120),
        ...(until ? { until } : {}),
        ...(filter === 'mine' && userPubkey ? { authors: [userPubkey] } : {}),
        ...(filter === 'included' && userPubkey ? { '#p': [userPubkey] } : {}),
        ...(filter === 'following' && following && following.length > 0
          ? { authors: following.slice(0, 100) }
          : {}),
      };

      const events: NostrEvent[] = await nostr.query([f], { signal: c.signal });

      const packs: FollowPack[] = [];
      const seen = new Set<string>();
      for (const event of events) {
        const pack = parseFollowPackEvent(event);
        if (!pack) continue;
        const key = `${pack.pubkey}:${pack.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        packs.push(pack);
      }

      packs.sort((a, b) => b.createdAt - a.createdAt);
      const hasMore = packs.length > limit;
      return { packs: packs.slice(0, limit), hasMore };
    },
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useFollowPack(pubkey: string | undefined, d: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<{ pack: FollowPack | null; event: NostrEvent | null }>({
    queryKey: ['follow-pack', pubkey ?? '', d ?? ''],
    queryFn: async (c) => {
      if (!pubkey || !d) return { pack: null, event: null };
      // Hoisted to a variable (not an inline literal) so extra `#d` tag
      // filters don't trip excess-property checks on the NostrFilter type.
      const filter = { kinds: [FOLLOW_PACK_KIND], authors: [pubkey], '#d': [d], limit: 1 };
      const events = await nostr.query([filter], { signal: c.signal });
      const event = events[0] as NostrEvent | undefined;
      if (!event) return { pack: null, event: null };
      return { pack: parseFollowPackEvent(event), event };
    },
    enabled: Boolean(pubkey && d),
    staleTime: 30 * 1000,
  });
}
